'use client';

import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { FiRefreshCw } from "react-icons/fi";
import NavBar from '@/components/header/NavBar';
import { useLocale } from 'next-intl';
import { GlowingEffect } from '@/components/home-page/glowing-effect';
import { Box, Lock, Search, Settings, Sparkles } from "lucide-react";

import Why from '@/components/home-page/whySection';
import ContactForm from '@/components/home-page/ContactForm';
import FAQContainer from '@/components/home-page/FAQContainer';
import Footer from '@/components/footer/Footer';


const ContactForms: React.FC = () => {

  return (<>
  {/* Fixed background — stays behind everything while page scrolls */}
  <div
    className="bg-cover bg-center bg-no-repeat h-96 lg:h-[500px] relative"
    style={{ backgroundImage: `url('/hero/education.avif')` }}
  >
   
<div className="absolute inset-0 bg-secondary/55 h-96 lg:h-[500px] " />
  <h1 className="absolute inset-0 flex items-center pt-20 lg:pt-40 justify-center text-white text-5xl lg:text-9xl font-extrabold text-center px-4 ">
   Education & Public
  </h1>
    
  </div>
 

  <NavBar />
   <ul className="grid grid-cols-1 bg-secondary grid-rows-none gap-4 md:grid-cols-12 md:grid-rows-3 lg:gap-4 p-8 xl:grid-rows-2">
      <GridItem
        area="md:[grid-area:1/1/2/7] xl:[grid-area:1/1/2/5]"
        icon={<Box className="h-4 w-4 text-black dark:text-primary" />}
        title="Schools & Universities"
        description="We build accessible, easy-to-navigate websites for schools and universities, helping students, parents, and staff find information and stay connected."
      />
 
      <GridItem
        area="md:[grid-area:1/7/2/13] xl:[grid-area:2/1/3/5]"
        icon={<Settings className="h-4 w-4 text-black dark:text-primary" />}
        title="E-Learning & Training"
        description="Get a digital platform that hosts courses, tracks progress, and makes it easy for learners to enroll, engage, and complete training online."
      />
 
      <GridItem
        area="md:[grid-area:2/1/3/7] xl:[grid-area:1/5/3/8]"
        icon={<Lock className="h-4 w-4 text-black dark:text-primary" />}
        title="Government & Public Sector"
        description="We build secure, reliable digital solutions for government and public sector organizations, helping protect sensitive data and deliver accessible services for citizens."
      />
 
      <GridItem
        area="md:[grid-area:2/7/3/13] xl:[grid-area:1/8/2/13]"
        icon={<Sparkles className="h-4 w-4 text-black dark:text-primary" />}
        title="Non-Profits & Charities"
        description="We create high-performing digital solutions that help non-profits and charities raise awareness, engage donors, and turn online visitors into lasting support."
      />
 
      <GridItem
        area="md:[grid-area:3/1/4/13] xl:[grid-area:2/8/3/13]"
        icon={<Search className="h-4 w-4 text-black dark:text-primary" />}
        title="Enrollment & Applications"
        description="We build smart enrollment and application systems that simplify admissions, manage submissions, and create seamless experiences for institutions and applicants."
      />
    </ul>
<Why/>
<ContactForm/>

<FAQContainer
  title={"Education &\nPublic FAQs"}
  description="Answers to the most common questions clients ask about our solutions for schools, e-learning, government, and non-profit organizations."
  faq={[
    {
      question: "Can you build a website for a school or university?",
      answer:
        "Yes, we build accessible, easy-to-navigate sites that help students, parents, and staff find information and stay connected.",
    },
    {
      question: "Do you build e-learning or online course platforms?",
      answer:
        "Yes, we create platforms that host courses, track learner progress, and make it easy for people to enroll and complete training online.",
    },
    {
      question: "Can you build solutions for government or public organizations?",
      answer:
        "Absolutely. We build secure, accessible digital solutions designed to meet public sector needs and serve citizens reliably.",
    },
    {
      question: "Do you build websites for non-profits and charities?",
      answer:
        "Yes, we create sites that help non-profits raise awareness, engage donors, and turn visitors into lasting supporters.",
    },
    {
      question: "Can you build an online enrollment or application system?",
      answer:
        "Yes, we build enrollment and application systems that simplify admissions and manage submissions for institutions and applicants.",
    },
    {
      question: "Do you offer support after the site or platform launches?",
      answer:
        "Yes, we offer ongoing maintenance and support plans to keep your platform or site running smoothly after launch.",
    },
  ]}
/>
<Footer/>
</>
  );
};

export default ContactForms;


interface GridItemProps {
  area: string;
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
}
 
const GridItem = ({ area, icon, title, description }: GridItemProps) => {
  return (
    <li className={`min-h-[14rem] list-none  rounded-3xl ${area}`}>
      <div className="relative h-full rounded-2xl border p-2 md:rounded-3xl md:p-3">
     <GlowingEffect

  glow={true}
  disabled={false}
  spread={40}
  proximity={64}
  inactiveZone={0.01}
  borderWidth={2}
/>
        <div className="border-0.75 relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-xl p-6 md:p-6 ">
          <div className="relative flex flex-1 flex-col justify-between gap-3">
            <div className="w-fit rounded-lg border text-primary border-white p-2">
              {icon}
            </div>
            <div className="space-y-3">
              <h3 className="-tracking-4 mt-8 text-xl  font-bold text-balance text-black md:text-3xl dark:text-primary">
                {title}
              </h3>
              <h2 className="text-sm/[1.125rem] text-black md:text-xl dark:text-white  font-light">
                {description}
              </h2>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};